import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, X, Boxes, Lock } from 'lucide-react';
import { useSelector } from 'react-redux';
import { selectIsLoggedIn, selectPlan } from '../store/authSlice';

const YES  = <Check className="w-4 h-4 text-emerald-500 mx-auto" />;
const NO   = <X    className="w-4 h-4 text-slate-300 mx-auto" />;

const FEATURES = [
  // label, starter, pro
  ['Payroll runs per month',               'Unlimited',    'Unlimited'],
  ['Employees',                            'Up to 8',      'Unlimited'],
  ['Salary calculation (basic/HRA/allowances)', YES,       YES],
  ['LOP deduction (prorated)',             YES,            YES],
  ['Overtime input',                       YES,            YES],
  ['Festival / ad-hoc bonus',             YES,            YES],
  ['Attendance calendar',                  YES,            YES],
  ['Leave tracking (PL / CL / SL / LOP)', YES,            YES],
  ['PDF payslip download',                 YES,            YES],
  ['PF / ESI toggle (manual)',             YES,            YES],
  ['Monthly payroll summary',              YES,            YES],
  ['Auto PF / ESI / PT calculation',       NO,             YES],
  ['WhatsApp payslip delivery',            NO,             YES],
  ['Salary advance tracking',              NO,             YES],
  ['Excel / CSV export',                   NO,             YES],
  ['Year-to-date salary summary',          NO,             YES],
  ['Multi-user roles (Owner/Manager/CA)',  NO,             YES],
  ['Multi-branch support',                 NO,             YES],
  ['Free onboarding call',                 NO,             YES],
  ['Priority WhatsApp support',            NO,             YES],
];

export default function Pricing() {
  const navigate   = useNavigate();
  const isLoggedIn = useSelector(selectIsLoggedIn);
  const plan       = useSelector(selectPlan);

  const cta = (planId) => {
    if (!isLoggedIn) return navigate('/register');
    if (plan === planId) return null;
    navigate('/settings');
  };

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      {/* Header */}
      <div className="bg-[#1E3A5F] text-white py-14 px-6 text-center">
        <div className="flex items-center justify-center gap-2.5 mb-6 cursor-pointer" onClick={() => navigate('/')}>
          <div className="w-9 h-9 rounded-md bg-white/10 ring-1 ring-white/20 flex items-center justify-center">
            <Boxes className="w-5 h-5" />
          </div>
          <span className="text-lg font-semibold font-heading">SlipSense</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-semibold font-heading mb-3">Simple, flat-fee pricing</h1>
        <p className="text-slate-300 text-base max-w-lg mx-auto">
          No per-employee charges. Your bill never changes, even during peak season.
        </p>
      </div>

      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-14">

        {/* Plan cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-14">
          {/* Starter */}
          <div className="bg-white border border-slate-200 rounded-2xl p-7 flex flex-col shadow-sm">
            <div className="text-[10px] uppercase tracking-widest text-slate-500 mb-2">Starter</div>
            <div className="text-4xl font-semibold font-mono text-slate-900">₹499<span className="text-base font-normal text-slate-500">/mo</span></div>
            <div className="text-sm text-slate-500 mt-1 mb-6">Up to 8 employees · 1 branch · 1 user</div>

            <ul className="space-y-2.5 text-sm text-slate-700 flex-1 mb-8">
              {['Unlimited payroll runs', 'Attendance + leave tracking', 'PDF payslip download', 'Manual PF/ESI toggle', 'Monthly summary report', 'Email support (48hr)'].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>

            <button
              onClick={() => cta('starter')}
              className={`w-full h-11 rounded-md font-medium text-sm transition-colors ${
                isLoggedIn && plan === 'starter'
                  ? 'bg-slate-100 text-slate-500 cursor-default'
                  : 'bg-slate-900 hover:bg-slate-700 text-white'
              }`}
            >
              {isLoggedIn && plan === 'starter' ? 'Current plan' : 'Get started'}
            </button>
          </div>

          {/* Pro */}
          <div className="bg-[#1E3A5F] text-white rounded-2xl p-7 flex flex-col shadow-lg relative overflow-hidden">
            <div className="absolute top-4 right-4 text-[10px] bg-amber-400 text-amber-900 font-semibold uppercase tracking-wider px-2 py-0.5 rounded">
              Most popular
            </div>
            <div className="text-[10px] uppercase tracking-widest text-slate-300 mb-2">Pro</div>
            <div className="text-4xl font-semibold font-mono">₹1,299<span className="text-base font-normal text-slate-300">/mo</span></div>
            <div className="text-sm text-slate-300 mt-1 mb-6">Unlimited employees · Multi-branch · 3 users</div>

            <ul className="space-y-2.5 text-sm text-slate-200 flex-1 mb-8">
              {[
                'Everything in Starter',
                'Auto PF / ESI / PT calculation',
                'WhatsApp payslip delivery',
                'Salary advance tracking',
                'Excel / CSV export for CA',
                'Multi-user roles (Owner/Manager/CA)',
                'Multi-branch support',
                'Free onboarding call',
                'Priority WhatsApp support (4hr)',
              ].map((f) => (
                <li key={f} className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  {f}
                </li>
              ))}
            </ul>

            <button
              onClick={() => cta('pro')}
              className={`w-full h-11 rounded-md font-medium text-sm transition-colors ${
                isLoggedIn && plan === 'pro'
                  ? 'bg-white/20 text-white cursor-default'
                  : 'bg-amber-400 hover:bg-amber-300 text-amber-900'
              }`}
            >
              {isLoggedIn && plan === 'pro' ? 'Current plan' : 'Get Pro'}
            </button>
          </div>
        </div>

        {/* Feature comparison table */}
        <h2 className="text-xl font-semibold font-heading text-slate-900 mb-4">Full feature comparison</h2>
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm">
          <div className="grid grid-cols-3 bg-slate-50 border-b border-slate-200 text-xs font-medium text-slate-500 uppercase tracking-wider">
            <div className="px-6 py-3">Feature</div>
            <div className="px-4 py-3 text-center">Starter</div>
            <div className="px-4 py-3 text-center text-[#2E6DA4]">Pro</div>
          </div>
          {FEATURES.map(([label, starter, pro], i) => (
            <div
              key={label}
              className={`grid grid-cols-3 border-b border-slate-100 last:border-0 text-sm ${i % 2 === 0 ? '' : 'bg-slate-50/50'}`}
            >
              <div className="px-6 py-3 text-slate-700">{label}</div>
              <div className="px-4 py-3 text-center font-mono text-slate-600 text-xs">
                {typeof starter === 'string' ? starter : starter}
              </div>
              <div className="px-4 py-3 text-center font-mono text-xs">
                {typeof pro === 'string' ? <span className="text-[#2E6DA4] font-medium">{pro}</span> : pro}
              </div>
            </div>
          ))}
        </div>

        {/* Upgrade note */}
        <div className="mt-10 bg-white border border-slate-200 rounded-xl p-6 flex items-start gap-4 shadow-sm">
          <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
            <Lock className="w-5 h-5 text-amber-600" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-900 mb-1">Pro features are gated in the app</div>
            <p className="text-sm text-slate-500">
              When you're on the Starter plan, Pro-only sections show a lock screen inside the app.
              Upgrading from Starter to Pro unlocks them instantly. Contact us on WhatsApp to upgrade.
            </p>
          </div>
        </div>

        <div className="text-center mt-8">
          {isLoggedIn
            ? <button onClick={() => navigate('/dashboard')} className="text-sm text-[#2E6DA4] hover:underline">← Back to dashboard</button>
            : <button onClick={() => navigate('/login')} className="text-sm text-[#2E6DA4] hover:underline">← Sign in</button>
          }
        </div>
      </div>
    </div>
  );
}